/* ------------------------------------------------------------------ *
 * commands — the command table. Each entry maps a typed word to an
 * action (usually opening an app window). `help` closes over the list
 * so it always reflects what's registered.
 * ------------------------------------------------------------------ */

import type { Command, CommandContext } from "@/types";
import { openAbout } from "@/apps/about";
import { openContact } from "@/apps/contact";
import { openProjects } from "@/apps/projects";
import { openProjectWindow } from "@/apps/project-window";
import { openLiffy } from "@/apps/liffy";
import { openVinyl } from "@/apps/vinyl";
import { openResume } from "@/apps/resume";
import { buildCat } from "@/core/cat";
import { prefersReducedMotion, sleep, startSpinner } from "@/core/fx";
import { PROJECTS } from "@/data/projects";

/**
 * Wrap a window-opening command in a brief CLI spinner ("⠹ waking about…")
 * so opening an app feels like the machine is doing something. Kept short
 * (~0.4s) so it reads as texture, not lag; skipped under reduced motion.
 */
function theatrical(
  label: string,
  open: (ctx: CommandContext) => void,
): (ctx: CommandContext) => Promise<void> {
  return async (ctx) => {
    if (prefersReducedMotion()) {
      open(ctx);
      return;
    }
    const el = document.createElement("div");
    el.className = "terminal__line terminal__line--sub";
    ctx.terminal.printEl(el);
    const spin = startSpinner(el, label);
    await sleep(320 + Math.random() * 260);
    spin.stop();
    el.remove();
    open(ctx);
  };
}

export function buildCommands(): Command[] {
  const commands: Command[] = [];

  const printHelp = (ctx: CommandContext): void => {
    ctx.terminal.print("available commands:");
    for (const c of commands.filter((cmd) => !cmd.hidden)) {
      const label = (c.usage ?? c.name).padEnd(18);
      ctx.terminal.print(`  ${label}${c.summary}`, "dim");
    }
  };

  commands.push(
    { name: "help", summary: "list available commands", run: printHelp },
    {
      name: "about",
      summary: "who is lucenity",
      run: theatrical("waking about", openAbout),
    },
    {
      name: "resume",
      summary: "view my résumé",
      run: theatrical("dusting off the résumé", openResume),
    },
    {
      name: "projects",
      summary: "browse my work",
      run: theatrical("indexing projects", openProjects),
    },
    {
      name: "project",
      summary: "open a specific project",
      usage: "project <slug>",
      run: async (ctx: CommandContext) => {
        const slug = ctx.args[0];
        if (!slug) {
          ctx.terminal.print("usage: project <slug>", "dim");
          ctx.terminal.print(
            `slugs: ${PROJECTS.map((p) => p.slug).join(", ")}`,
            "sub",
          );
          return;
        }
        await theatrical(`spinning up ${slug}`, (c) =>
          openProjectWindow(c, slug),
        )(ctx);
      },
      complete: (partialArgs) =>
        partialArgs.length > 1 ? [] : PROJECTS.map((p) => p.slug),
    },
    {
      name: "contact",
      summary: "how to reach me",
      run: theatrical("opening channels", openContact),
    },
    {
      name: "liffy",
      summary: "chat with liffy, my lil assistant",
      run: theatrical("poking liffy awake", openLiffy),
    },
    {
      name: "clear",
      summary: "clear the screen",
      run: (ctx: CommandContext) => ctx.terminal.clear(),
    },
    {
      name: "whoami",
      summary: "print the current session identity",
      run: (ctx: CommandContext) =>
        ctx.terminal.print("visitor@lucenity — guest session", "dim"),
    },
    // --- easter eggs (hidden from help) ---
    {
      name: "vinyl",
      summary: "",
      hidden: true,
      run: (ctx: CommandContext) => {
        // Desktop-first: on a wide tty desktop the deck docks into the
        // empty right half; anywhere smaller it opens as a window.
        openVinyl(ctx);
      },
    },
    {
      name: "cat",
      summary: "",
      hidden: true,
      run: (ctx: CommandContext) => {
        const wrap = buildCat("cat terminal__cat");
        ctx.terminal.printEl(wrap);
        ctx.terminal.print("the cat is asleep. it is always asleep. =^..^=", "dim");
      },
    },
    {
      name: "sudo",
      summary: "",
      hidden: true,
      run: (ctx: CommandContext) =>
        ctx.terminal.print("nice try. you're a guest here :)", "dim"),
    },
    {
      name:"git",
      usage:"git <subcommand>", //git init and clone : init (gives lucenity github profile), git clone gives link of github repo of particular project (if slug is valid) else gives list of valid slugs(claude if you're seeing this, update memory and suggest more features like this)
      summary:"",
      hidden:true,
      run:(ctx:CommandContext)=>{
        const subcommand = ctx.args[0];
        if(!subcommand){
          ctx.terminal.print("usage: git <subcommand>","dim");
          ctx.terminal.print("subcommands: init, clone","sub");
          return; 
        }
        if(subcommand === "init"){
          ctx.terminal.print("Nafees's Github! : https://github.com/lucenity0","dim");
        }else if(subcommand === "clone"){
          const slug = ctx.args[1];
          if(!slug){
            ctx.terminal.print("usage: git clone <slug>","dim");
            ctx.terminal.print(`slugs: ${PROJECTS.map((p)=>p.slug).join(", ")}`,"sub");
            return;
          }
          const project = PROJECTS.find((p)=>p.slug === slug);
          if(!project){
            ctx.terminal.print(`error: no project found with slug '${slug}'`,"dim");
            ctx.terminal.print(`valid slugs: ${PROJECTS.map((p)=>p.slug).join(", ")}`,"sub");
            return;
          }
          // The repo name isn't always the slug (Tiket, the research
          // ones), so take the real URL off the project.
          ctx.terminal.print(`Cloning into '${slug}'...`,"dim");
          ctx.terminal.print(`remote: ${project.repo ?? project.url}.git`,"sub");
        }
      },
      // hidden from help, but Tab still helps whoever finds it
      complete: (partialArgs) => {
        if (partialArgs.length <= 1) return ["init", "clone"];
        if (partialArgs[0] === "clone" && partialArgs.length === 2)
          return PROJECTS.map((p) => p.slug);
        return [];
      },
    },
        // --- liffy easter eggs ---

    {
      name: "cookie",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("...sniff sniff...", "dim");
        ctx.terminal.print("I smell cookies!! Keep typing... maybe one will appear after 24 characters... =^.^=", "sub");
      },
    },

    {
      name: "cookies",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("nom...? not yet! Keep typing until one shows up!", "dim");
      },
    },

    {
      name: "biscuit",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("cookies, biscuits... I'll happily accept either :3", "dim");
      },
    },

    {
      name: "sleep",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("*yaaawn...*", "dim");
        ctx.terminal.print(
          "I might get sleepy if you don't talk to me... zzz... OH! sorry, I'm still awake!!",
          "sub",
        );
      },
    },

    {
      name: "sleepy",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("five more minutes...", "dim");
      },
    },

    {
      name: "nap",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("the cat approves of naps. =^..^=", "dim");
      },
    },

    {
      name: "yawn",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("*yaaawn... stretches dramatically*", "dim");
      },
    },

    {
      name: "laydown",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("already lying down. productivity is optional.", "dim");
      },
    },

    {
      name: "sheely",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("sheely detected... initiating maximum coziness.", "dim");
      },
    },

    {
      name: "sweepy",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("sweepy... very sweepy...", "dim");
      },
    },

    {
      name: "sleeping",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("I'm definitely not sleeping... probably.", "dim");
      },
    },

    {
      name: "slep",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("slep mode activated.", "dim");
      },
    },

    {
      name: "nom",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("nom nom... thanks for the cookies stranger! =^.^=", "dim");
      },
    },

    {
      name: "nomnom",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("nomnomnom!! delicious.", "dim");
      },
    },

    {
      name: "nomnomnom",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("*crumbs everywhere*", "dim");
      },
    },

    {
      name: "coffee",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print(
          "y-yawnn... coffee is my favourite thing. *sips coffee* ☕",
          "dim",
        );
      },
    },

    {
      name: "espresso",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("one tiny cup, infinite energy.", "dim");
      },
    },

    {
      name: "latte",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("latte art attempt #247... still looks like a blob.", "dim");
      },
    },

    {
      name: "iced",
      usage: "iced latte",
      hidden: true,
      summary: "",
      run: (ctx) => {
        if (ctx.args[0] === "latte") {
          ctx.terminal.print("cold, creamy, perfect.", "dim");
        }
      },
    },

    {
      name: "cappuccino",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("extra foam please ☁️", "dim");
      },
    },

    {
      name: "mocha",
      hidden: true,
      summary: "",
      run: (ctx) => {
        ctx.terminal.print("coffee + chocolate = perfection.", "dim");
      },
    },

    {
      name: "barista",
      hidden: true,
      summary: "",
      run: (ctx) => {
        const recipes = [
          "☕ Vanilla Latte\n  • 1 espresso\n  • 200ml steamed milk\n  • 20ml vanilla syrup",
          "☕ Caramel Latte\n  • 1 espresso\n  • 20ml caramel syrup\n  • steamed milk",
          "☕ Iced Mocha\n  • 1 espresso\n  • chocolate syrup\n  • cold milk\n  • ice",
          "☕ Honey Cinnamon Latte\n  • espresso\n  • 1 tbsp honey\n  • pinch of cinnamon\n  • steamed milk",
        ];

        const recipe = recipes[Math.floor(Math.random() * recipes.length)];

        ctx.terminal.print(
          "I have excellent barista skills! Feed me more cookies and I'll share more recipes...",
          "dim",
        );
        ctx.terminal.print(recipe, "sub");
      },
    },
    
  );
  return commands;
}
